import PropTypes from 'prop-types';
import { createRef } from 'react';

import { defineMessages, injectIntl } from 'react-intl';

import classNames from 'classnames';

import ImmutablePropTypes from 'react-immutable-proptypes';
import ImmutablePureComponent from 'react-immutable-pure-component';

import { length } from 'stringz';


import ChevronRightIcon from '@/material-icons/400-24px/chevron_right.svg?react';
import MicIcon from '@/material-icons/400-24px/mic.svg?react';
import AutosuggestInput from 'mastodon/components/autosuggest_input';
import AutosuggestTextarea from 'mastodon/components/autosuggest_textarea';
import { Button } from 'mastodon/components/button';
import { Icon } from 'mastodon/components/icon';
import { IconButton } from 'mastodon/components/icon_button';
import { missingAltTextModal } from 'mastodon/initial_state';

import EmojiPickerDropdown from '../containers/emoji_picker_dropdown_container';
import PollButtonContainer from '../containers/poll_button_container';
import SpoilerButtonContainer from '../containers/spoiler_button_container';
import UploadButtonContainer from '../containers/upload_button_container';
import { countableText } from '../util/counter';

import { CharRing } from './char_ring';
import { ComposeDraft } from './compose_draft';
import { ComposeReachDropdown } from './compose_reach_dropdown';
import { ComposeVoiceRecorder } from './compose_voice_recorder';
import { EditIndicator } from './edit_indicator';
import { KategoryPicker } from './kategory_picker';
import { NavigationBar } from './navigation_bar';
import { PollForm } from "./poll_form";
import { ComposeQuotedStatus } from './quoted_post';
import { ReplyIndicator } from './reply_indicator';
import { UploadForm } from './upload_form';
import { Warning } from './warning';

const allowedAroundShortCode = '><\u0085\u0020\u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u202f\u205f\u3000\u2028\u2029\u0009\u000a\u000b\u000c\u000d';

const messages = defineMessages({
  placeholder: { id: 'compose_form.placeholder', defaultMessage: 'What is on your mind?' },
  spoiler_placeholder: { id: 'compose_form.spoiler_placeholder', defaultMessage: 'Content warning (optional)' },
  publish: { id: 'compose_form.publish', defaultMessage: 'Kronk it' },
  saveChanges: { id: 'compose_form.save_changes', defaultMessage: 'Update' },
  reply: { id: 'compose_form.reply', defaultMessage: 'Reply' },
  moreOptions: { id: 'compose_form.more_options', defaultMessage: 'More options' },
  recordVoice: { id: 'compose_form.record_voice', defaultMessage: 'Record a voice clip' },
});

class ComposeForm extends ImmutablePureComponent {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    text: PropTypes.string.isRequired,
    suggestions: ImmutablePropTypes.list,
    spoiler: PropTypes.bool,
    privacy: PropTypes.string,
    spoilerText: PropTypes.string,
    focusDate: PropTypes.instanceOf(Date),
    caretPosition: PropTypes.number,
    preselectDate: PropTypes.instanceOf(Date),
    isSubmitting: PropTypes.bool,
    isChangingUpload: PropTypes.bool,
    isEditing: PropTypes.bool,
    isUploading: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClearSuggestions: PropTypes.func.isRequired,
    onFetchSuggestions: PropTypes.func.isRequired,
    onSuggestionSelected: PropTypes.func.isRequired,
    onChangeSpoilerText: PropTypes.func.isRequired,
    onPaste: PropTypes.func.isRequired,
    onPickEmoji: PropTypes.func.isRequired,
    autoFocus: PropTypes.bool,
    withoutNavigation: PropTypes.bool,
    anyMedia: PropTypes.bool,
    missingAltText: PropTypes.bool,
    isInReply: PropTypes.bool,
    singleColumn: PropTypes.bool,
    lang: PropTypes.string,
    maxChars: PropTypes.number,
    redirectOnSuccess: PropTypes.bool,
    // Kronk overrides for surfaces that reuse the compose reducer with
    // different framing (e.g. the inline Home status box uses the day's
    // Kuestion as placeholder and "Kronk it" as the submit label).
    placeholderText: PropTypes.string,
    publishLabel: PropTypes.string,
    // Optional string that Tab injects into the compose text (used by
    // the Home inline box: press Tab on the daily Kuestion prompt to
    // pull it into the post). Only fires when set.
    insertOnTab: PropTypes.string,
  };

  static defaultProps = {
    autoFocus: false,
  };

  state = {
    highlighted: false,
    toolsOpen: false,
    voiceOpen: false,
  };

  handleToggleTools = () => {
    this.setState((state) => ({ toolsOpen: !state.toolsOpen }));
  };

  handleToggleVoice = () => {
    this.setState((state) => ({ voiceOpen: !state.voiceOpen }));
  };

  handleCloseVoice = () => {
    this.setState({ voiceOpen: false });
  };

  constructor(props) {
    super(props);
    this.textareaRef = createRef(null);
  }

  handleChange = (e) => {
    this.props.onChange(e.target.value);
  };

  blurOnEscape = (e) => {
    if (['esc', 'escape'].includes(e.key.toLowerCase())) {
      e.target.blur();
    }
  }

  handleKeyDownPost = (e) => {
    if (e.key.toLowerCase() === 'enter' && (e.ctrlKey || e.metaKey)) {
        this.handleSubmit();
        e.preventDefault();
    }
    if (e.key === 'Tab' && this.props.insertOnTab && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Insert the prompt at the current caret. Keeps the composer as the
      // user's live editor — they can keep typing, delete it, whatever.
      // Shift+Tab / any modifier still yields to native focus movement.
      const target = e.target;
      const start = target.selectionStart ?? this.props.text.length;
      const end = target.selectionEnd ?? start;
      const before = this.props.text.slice(0, start);
      const after = this.props.text.slice(end);
      const needsSpaceBefore = before.length > 0 && !before.endsWith(' ');
      const injected = `${needsSpaceBefore ? ' ' : ''}${this.props.insertOnTab} `;
      this.props.onChange(`${before}${injected}${after}`);
      e.preventDefault();
    }
    this.blurOnEscape(e);
  };

  handleKeyDownSpoiler = (e) => {
    if (e.key.toLowerCase() === 'enter') {
      if (e.ctrlKey || e.metaKey) {
        this.handleSubmit();
      } else {
        e.preventDefault();
        this.textareaRef.current?.focus();
      }
    }
    this.blurOnEscape(e);
  };

  getFulltextForCharacterCounting = () => {
    return [this.props.spoiler? this.props.spoilerText: '', countableText(this.props.text)].join('');
  };

  canSubmit = () => {
    const { isSubmitting, isChangingUpload, isUploading, maxChars } = this.props;
    const fulltext = this.getFulltextForCharacterCounting();

    return !(isSubmitting || isUploading || isChangingUpload || length(fulltext) > maxChars);
  };

  handleSubmit = (e) => {
    if (this.props.text !== this.textareaRef.current.value) {
      // Something changed the text inside the textarea (e.g. browser extensions like Grammarly)
      // Update the state to match the current text
      this.props.onChange(this.textareaRef.current.value);
    }

    if (!this.canSubmit()) {
      return;
    }

    this.props.onSubmit({
      // `direct` is retired from the composer (Phase 1B, 2026-08-12) —
      // the visibility guard on the alt-text modal no longer needs
      // the direct exemption.
      missingAltText: missingAltTextModal && this.props.missingAltText,
      quoteToPrivate: this.props.quoteToPrivate,
    });

    if (e) {
      e.preventDefault();
    }
  };

  onSuggestionsClearRequested = () => {
    this.props.onClearSuggestions();
  };

  onSuggestionsFetchRequested = (token) => {
    this.props.onFetchSuggestions(token);
  };

  onSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['text']);
  };

  onSpoilerSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['spoiler_text']);
  };

  handleChangeSpoilerText = (e) => {
    this.props.onChangeSpoilerText(e.target.value);
  };

  handleFocus = () => {
    if (this.composeForm && !this.props.singleColumn) {
      const { left, right } = this.composeForm.getBoundingClientRect();
      if (left < 0 || right > (window.innerWidth || document.documentElement.clientWidth)) {
        this.composeForm.scrollIntoView();
      }
    }
  };

  componentDidMount () {
    this._updateFocusAndSelection({ });
  }

  componentWillUnmount () {
    if (this.timeout) clearTimeout(this.timeout);
  }

  componentDidUpdate (prevProps) {
    this._updateFocusAndSelection(prevProps);
  }

  _updateFocusAndSelection = (prevProps) => {
    // This statement does several things:
    // - If we're beginning a reply, and,
    //     - Replying to zero or one users, places the cursor at the end of the textbox.
    //     - Replying to more than one user, selects any usernames past the first;
    //       this provides a convenient shortcut to drop everyone else from the conversation.
    if (this.props.focusDate && this.props.focusDate !== prevProps.focusDate) {
      let selectionEnd, selectionStart;

      if (this.props.preselectDate !== prevProps.preselectDate && this.props.isInReply) {
        selectionEnd   = this.props.text.length;
        selectionStart = this.props.text.search(/\s/) + 1;
      } else if (typeof this.props.caretPosition === 'number') {
        selectionStart = this.props.caretPosition;
        selectionEnd   = this.props.caretPosition;
      } else {
        selectionEnd   = this.props.text.length;
        selectionStart = selectionEnd;
      }

      // Because of the wicg-inert polyfill, the activeElement may not be
      // immediately selectable, we have to wait for observers to run, as
      // described in https://github.com/WICG/inert#performance-and-gotchas
      Promise.resolve().then(() => {
        this.textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
        this.textareaRef.current.focus();
        this.setState({ highlighted: true });
        this.timeout = setTimeout(() => this.setState({ highlighted: false }), 700);
      }).catch(console.error);
    } else if(prevProps.isSubmitting && !this.props.isSubmitting) {
      this.textareaRef.current.focus();
    } else if (this.props.spoiler !== prevProps.spoiler) {
      const mediaJustAdded = this.props.anyMedia && !prevProps.anyMedia;

      if (this.props.spoiler && !mediaJustAdded) {
        this.spoilerText.input.focus();
      } else if (prevProps.spoiler) {
        this.textareaRef.current.focus();
      }
    }
  };

  setSpoilerText = (c) => {
    this.spoilerText = c;
  };

  setRef = c => {
    this.composeForm = c;
  };

  handleEmojiPick = (data) => {
    const { text }     = this.props;
    const position     = this.textareaRef.current.selectionStart;
    const needsSpace   = data.custom && position > 0 && !allowedAroundShortCode.includes(text[position - 1]);

    this.props.onPickEmoji(position, data, needsSpace);
  };

  render () {
    const { intl, onPaste, autoFocus, withoutNavigation, maxChars, isSubmitting } = this.props;
    const { highlighted, toolsOpen, voiceOpen } = this.state;

    return (
      <form className='compose-form' onSubmit={this.handleSubmit}>
        <ReplyIndicator />
        <Warning />

        <div className={classNames('compose-form__highlightable', { active: highlighted })} ref={this.setRef}>
          <EditIndicator />
          <ComposeDraft />

          {/* Header — identity (avatar + name) with the audience dropdown to the
              right, mirroring how a post's author + reach read in the feed. The
              language picker stays hidden: the post's language is auto-tagged
              from the user's posting-language default (sent on submit); change
              it in Settings → Posting.

              No reach picker on a comment. A comment is visible to whoever the
              post it is on is visible to (docs/spaces/feed.md (Comments)) — the
              server writes it with the root's reach and ignores whatever was
              asked for. Leaving the control up would offer a choice that does
              not exist, which is worse than offering none. */}
          <div className='compose-form__head'>
            {!withoutNavigation && <NavigationBar />}
            <div className='compose-form__head-spacer' />
            <KategoryPicker />
            {!this.props.isInReply && <ComposeReachDropdown />}
          </div>


          {this.props.spoiler && (
            <div className='spoiler-input'>
              <div className='spoiler-input__border' />

              <AutosuggestInput
                placeholder={intl.formatMessage(messages.spoiler_placeholder)}
                value={this.props.spoilerText}
                disabled={isSubmitting}
                onChange={this.handleChangeSpoilerText}
                onKeyDown={this.handleKeyDownSpoiler}
                ref={this.setSpoilerText}
                suggestions={this.props.suggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                onSuggestionSelected={this.onSpoilerSuggestionSelected}
                searchTokens={[':']}
                id='cw-spoiler-input'
                className='spoiler-input__input'
                lang={this.props.lang}
                spellCheck
              />

              <div className='spoiler-input__border' />
            </div>
          )}

          <AutosuggestTextarea
            ref={this.textareaRef}
            placeholder={this.props.placeholderText ?? intl.formatMessage(messages.placeholder)}
            disabled={isSubmitting}
            value={this.props.text}
            onChange={this.handleChange}
            suggestions={this.props.suggestions}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDownPost}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionSelected={this.onSuggestionSelected}
            onPaste={onPaste}
            autoFocus={autoFocus}
            lang={this.props.lang}
            className='compose-form__input'
          />

          <UploadForm />
          <PollForm />
          <ComposeVoiceRecorder open={voiceOpen} onClose={this.handleCloseVoice} />
          <ComposeQuotedStatus />

          <div className='compose-form__footer'>
            <div className='compose-form__actions'>
              {/* Options tuck behind a chevron they slide out of, so the
                  resting composer stays quiet. */}
              <button
                type='button'
                className={classNames('compose-form__reveal', { open: toolsOpen })}
                aria-expanded={toolsOpen}
                aria-label={intl.formatMessage(messages.moreOptions)}
                onClick={this.handleToggleTools}
              >
                <Icon id='' icon={ChevronRightIcon} />
              </button>

              <div className={classNames('compose-form__tools', { open: toolsOpen })}>
                <div className='compose-form__tools-tray'>
                  <UploadButtonContainer />
                  <PollButtonContainer />
                  <SpoilerButtonContainer />
                  <EmojiPickerDropdown onPickEmoji={this.handleEmojiPick} />
                  <IconButton
                    icon='microphone'
                    iconComponent={MicIcon}
                    title={intl.formatMessage(messages.recordVoice)}
                    onClick={this.handleToggleVoice}
                    active={voiceOpen}
                    disabled={this.props.isEditing}
                    size={18}
                    inverted
                  />
                </div>
              </div>

              <div className='compose-form__actions-spacer' />

              <CharRing max={maxChars} text={this.getFulltextForCharacterCounting()} />

              <div className='compose-form__submit'>
                <Button
                  type='submit'
                  compact
                  disabled={!this.canSubmit()}
                  loading={isSubmitting}
                >
                  {this.props.isEditing || this.props.isInReply || !this.props.publishLabel
                    ? intl.formatMessage(
                        this.props.isEditing
                          ? messages.saveChanges
                          : (this.props.isInReply ? messages.reply : messages.publish)
                      )
                    : this.props.publishLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }

}

export default injectIntl(ComposeForm);
